'use client'

import { useSignals } from '@preact/signals-react/runtime'
import type { ProductTrackingData } from '@susu/headless-commerce/components/ProductCard/ClientProductCardImageLink'
import { useCountry } from '@susu/headless-commerce/contexts/country/client'
import { useLocale } from '@susu/headless-commerce/contexts/locale/client'
import type {
  CommerceProductsWithImgTransformQuery,
  CommerceProductsWithImgTransformQueryVariables,
} from '@susu/headless-commerce/gql/generated/commerceProductsWithImgTransform.operation'
import type {
  Button,
  Product,
} from '@susu/headless-commerce/gql/generated/graphql'
import type {
  ShopCollectionQuery,
  ShopCollectionQueryVariables,
} from '@susu/headless-commerce/gql/generated/shopCollection.operation'
import { usePageType } from '@susu/headless-commerce/hooks/usePageType'
import type { PromotionEvents } from '@susu/headless-commerce/utils/tracking/tracking'
import {
  trackClickPromotion,
  trackImpressionPromotion,
  trackViewProductsList,
} from '@susu/headless-commerce/utils/tracking/tracking'
import { isUndefined } from '@susu/undefined'
import classNames from 'classnames'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useRef } from 'react'
import type { OperationResult } from 'urql'
import type { VerticalSpacingProps } from '../VerticalSpacing/VerticalSpacing'
import VerticalSpacing from '../VerticalSpacing/VerticalSpacing'

import { ClientContentButton } from '../../ContentButton/ClientContentButton'
import styles from './ShopCollectionBlockSection.module.scss'

export type ClientShopCollectioBlockProps = {
  commerceQuery: OperationResult<
    CommerceProductsWithImgTransformQuery,
    CommerceProductsWithImgTransformQueryVariables
  >
  contentfulQuery: OperationResult<
    ShopCollectionQuery,
    ShopCollectionQueryVariables
  >
  automationId: string
  tracking: Omit<ProductTrackingData, 'position'>
  children?: ReactNode
}

export default function ClientShopCollectionBlock({
  commerceQuery,
  contentfulQuery,
  automationId,
  tracking,
  children,
}: Readonly<ClientShopCollectioBlockProps>) {
  useSignals()
  const pageType = usePageType()
  const locale = useLocale()
  const country = useCountry()

  const { shopCollectionBlock } = contentfulQuery.data ?? {}
  const { productSearchWithImgTransform } = commerceQuery.data ?? {}

  if (!shopCollectionBlock) {
    throw new Error('Contentful query missing')
  }

  if (!productSearchWithImgTransform) {
    throw new Error('Commerce query missing')
  }

  const buttonRef = useRef<HTMLDivElement>(null)
  const collectionListRef = useRef<HTMLDivElement>(null)
  const listViewTriggered = useRef<boolean>(false)

  const {
    displayAsGridOnDesktop,
    displayAsGridOnTablet,
    displayAsGridOnMobile,
    seeFullCollectionButtonPosition,
  } = shopCollectionBlock

  const contentItems =
    contentfulQuery.data?.shopCollectionBlock?.contentSectionsCollection
      ?.items ?? []

  if (contentItems.length === 0) {
    throw new Error('Content items missing')
  }

  const buttonItem = contentItems?.find(
    (item) => item?.__typename === 'Button',
  ) as Button

  const verticalSpacingData = contentItems?.find(
    (item) => item?.__typename === 'Spaces',
  ) as VerticalSpacingProps['data']

  const onClick = useCallback(() => {
    trackClickPromotion(
      buttonRef,
      buttonItem?.link?.promotionEvents as PromotionEvents,
      pageType,
      {
        locale,
        country,
      },
    )
  }, [buttonItem?.link?.promotionEvents, country, locale, pageType])

  const wrapperClass = classNames(styles['shop-collection__wrapper'], {
    [styles['shop-collection__wrapper--reversed']]:
      !!seeFullCollectionButtonPosition,
  })

  const gridClass = classNames(styles['shop-collection__items'], {
    [styles['shop-collection__grid']]:
      displayAsGridOnDesktop ?? displayAsGridOnTablet ?? displayAsGridOnMobile,
    [styles['shop-collection__grid-desktop']]: displayAsGridOnDesktop,
    [styles['shop-collection__grid-tablet']]: displayAsGridOnTablet,
    [styles['shop-collection__grid-mobile']]: displayAsGridOnMobile,
    [styles['shop-collection--two-items']]:
      productSearchWithImgTransform?.length === 2,
  })

  useEffect(() => {
    if (isUndefined(productSearchWithImgTransform)) {
      return
    }

    if (buttonRef.current) {
      trackImpressionPromotion(
        buttonRef,
        buttonItem?.link?.promotionEvents as PromotionEvents,
        pageType,
        {
          locale,
          country,
        },
      )
    }

    if (collectionListRef.current && !listViewTriggered.current) {
      listViewTriggered.current = true

      trackViewProductsList({
        elementRef: collectionListRef,
        enrichProps: {
          country,
          locale,
        },
        pageType,
        products: productSearchWithImgTransform as Product[],
        tracking,
      })
    }
  }, [
    buttonItem?.link?.promotionEvents,
    country,
    locale,
    pageType,
    productSearchWithImgTransform,
    tracking,
  ])

  return (
    <div className={wrapperClass}>
      <div className={gridClass} ref={collectionListRef}>
        {children}
      </div>
      {verticalSpacingData && <VerticalSpacing data={verticalSpacingData} />}
      {buttonItem && (
        <div className={styles['shop-collection__button']}>
          <ClientContentButton
            buttonData={buttonItem as Button}
            onClick={onClick}
            pageType={pageType}
            automationId={automationId}
          />
        </div>
      )}
    </div>
  )
}
